import { createAsyncThunk } from '@reduxjs/toolkit';

import { createEndpoints } from '../../api/endpoints';
import { API_CONFIG } from '../../config/api';
import { BrowserLoginRequiredError } from '../../services/bootstrap/BootstrapService';
import { bootstrapService } from '../../services/singletons.js';
import { appActions } from '../slices/appSlice';
import { authActions } from '../slices/authSlice';
import { entitiesActions } from '../slices/entitiesSlice';
import type { RootState } from '../store';

// Outside Telegram without a session the Welcome page shows the browser login instead of an error.
function toBootstrapErrorMessage(error: unknown): string | null {
  if (error instanceof BrowserLoginRequiredError) return null;
  return error instanceof Error ? error.message : 'Не удалось загрузить данные';
}

async function runBootstrapData(thunkApi: {
  dispatch: (action: unknown) => void;
  getState: () => RootState;
}): Promise<void> {
  const { dispatch } = thunkApi;

  const { session, events, groups, users } = await bootstrapService.bootstrap();

  // Preserve existing behavior: session stored before entities.
  dispatch(authActions.setSession({ user: session.user }));
  dispatch(appActions.setBootstrapError(null));
  dispatch(entitiesActions.upsertEvents(events.items));
  dispatch(entitiesActions.setGroups(groups.items));
  dispatch(entitiesActions.setUsers(users.items));
}

// Loads Telegram session + initial data. Does NOT accept rules.
export const bootstrapData = createAsyncThunk<void, void, { state: RootState }>(
  'app/bootstrapData',
  async (_, thunkApi) => {
    const { dispatch, getState } = thunkApi;

    dispatch(appActions.setIsLoading(true));

    try {
      await runBootstrapData({ dispatch, getState });
    } catch (error) {
      const message = toBootstrapErrorMessage(error);
      dispatch(authActions.clearSession());
      dispatch(entitiesActions.resetEntities());
      dispatch(appActions.setBootstrapError(message));
      throw error;
    } finally {
      dispatch(appActions.setIsLoading(false));
    }
  },
);

// Accepts rules + loads initial data.
export const bootstrapApp = createAsyncThunk<void, void, { state: RootState }>(
  'app/bootstrap',
  async (_, thunkApi) => {
    const { dispatch, getState } = thunkApi;

    dispatch(appActions.setIsLoading(true));

    try {
      await runBootstrapData({ dispatch, getState });
      dispatch(appActions.setRulesAccepted(true));
    } catch (error) {
      const message = toBootstrapErrorMessage(error);
      dispatch(authActions.clearSession());
      dispatch(entitiesActions.resetEntities());
      dispatch(appActions.setBootstrapError(message));
      throw error;
    } finally {
      dispatch(appActions.setIsLoading(false));
    }
  },
);

// Logs out the user by calling backend logout endpoint and clearing local state.
export const logoutUser = createAsyncThunk<void, void, { state: RootState }>(
  'auth/logout',
  async (_, thunkApi) => {
    const { dispatch } = thunkApi;

    try {
      const endpoints = createEndpoints({ baseUrl: API_CONFIG.BASE_URL });
      await endpoints.authLogout();
    } catch (error) {
      // Even if backend call fails, clear local session
      console.warn('Logout API call failed, clearing local session anyway', error);
    } finally {
      // Always clear local state regardless of backend response
      dispatch(authActions.clearSession());
      dispatch(entitiesActions.resetEntities());
      dispatch(appActions.setRulesAccepted(false));
    }
  },
);
